<template>
  <div class="relatorios-campanhas">
    <!-- Loading inicial -->
    <div
      v-if="carregandoInicial"
      class="loading-container"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-3 text-muted">
        Carregando relatórios...
      </p>
    </div>

    <!-- Seleção de Loja (se tiver mais de uma) -->
    <div
      v-else-if="!storeUuid && storesList.length > 1"
      class="stores-container"
    >
      <b-card class="text-center py-5">
        <feather-icon
          icon="ShoppingBagIcon"
          size="48"
          class="text-primary mb-3"
        />
        <h4>Selecione uma loja</h4>
        <p class="text-muted mb-4">
          Escolha a loja para visualizar os relatórios de campanhas
        </p>
        <div class="stores-grid-select">
          <div
            v-for="store in storesList"
            :key="store.uuid"
            class="store-select-card"
            @click="selecionarLoja(store)"
          >
            <div
              class="store-avatar-small"
              :style="getAvatarStyle(store)"
            >
              <span v-if="!store.logo">{{ getInitials(store.name) }}</span>
              <img
                v-else
                :src="store.logo"
                :alt="store.name"
              >
            </div>
            <span class="store-name-small">{{ store.name }}</span>
            <feather-icon
              icon="ChevronRightIcon"
              size="16"
              class="text-muted"
            />
          </div>
        </div>
      </b-card>
    </div>

    <!-- Conteúdo principal -->
    <template v-else>
      <!-- Header -->
      <b-card class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div class="d-flex align-items-center">
              <h3 class="mb-0">
                Relatórios de Campanhas Segmentadas
              </h3>
              <b-badge
                v-if="lojaAtual"
                variant="light-primary"
                class="ml-2"
              >
                {{ lojaAtual.name }}
              </b-badge>
            </div>
            <p class="text-muted mb-0">
              Análise detalhada do desempenho das suas campanhas
            </p>
          </div>
          <div class="d-flex gap-2">
            <b-button
              v-if="storesList.length > 1"
              variant="outline-secondary"
              @click="trocarLoja"
            >
              <feather-icon
                icon="ShoppingBagIcon"
                size="16"
                class="mr-50"
              />
              Trocar Loja
            </b-button>
            <b-button
              variant="outline-primary"
              :disabled="carregando"
              @click="atualizarDados"
            >
              <feather-icon
                icon="RefreshCwIcon"
                :class="{ 'spin': carregando }"
                size="16"
                class="mr-50"
              />
              Atualizar
            </b-button>
            <b-button
              variant="primary"
              @click="exportarRelatorio"
            >
              <feather-icon
                icon="DownloadIcon"
                size="16"
                class="mr-50"
              />
              Exportar CSV
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- Métricas Principais -->
      <div class="stats-grid mb-3 col-12">
        <b-row>

          <b-col cols="6">
            <b-card class="stat-card">
              <div class="stat-icon bg-light-primary">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
              <div class="stat-content">
                <h3 class="stat-value mb-0">
                  {{ formatarMoeda(metricas.receitaTotal) }}
                </h3>
                <p class="stat-label text-muted mb-0">
                  Receita Total
                </p>
                <small class="text-muted">Ticket médio: {{ formatarMoeda(metricas.ticketMedio) }}</small>
              </div>
            </b-card>
          </b-col>

          <b-col cols="6">
            <b-card class="stat-card">
              <div class="stat-icon bg-light-success">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                  class="text-success"
                />
              </div>
              <div class="stat-content">
                <h3
                  class="stat-value mb-0"
                  :class="metricas.lucroTotal >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatarMoeda(metricas.lucroTotal) }}
                </h3>
                <p class="stat-label text-muted mb-0">
                  Lucro Total
                </p>
                <small class="text-muted">Receita - Custo</small>
              </div>
            </b-card>
          </b-col>

          <b-col cols="6">
            <b-card class="stat-card">
              <div class="stat-icon bg-light-warning">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                  class="text-warning"
                />
              </div>
              <div class="stat-content">
                <h3 class="stat-value mb-0">
                  {{ formatarPorcentagem(metricas.taxaConversao) }}
                </h3>
                <p class="stat-label text-muted mb-0">
                  Taxa de Conversão
                </p>
                <small class="text-muted">{{ formatarNumero(metricas.totalConversoes) }} conversões</small>
              </div>
            </b-card>
          </b-col>

          <b-col cols="6">
            <b-card class="stat-card">
              <div
                class="stat-icon"
                :class="metricas.roi >= 0 ? 'bg-light-success' : 'bg-light-danger'"
              >
                <feather-icon
                  icon="RepeatIcon"
                  size="24"
                  :class="metricas.roi >= 0 ? 'text-success' : 'text-danger'"
                />
              </div>
              <div class="stat-content">
                <h3
                  class="stat-value mb-0"
                  :class="metricas.roi >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ formatarRoiVezesMetricasConsolidadas() }}
                </h3>
                <p class="stat-label text-muted mb-0">
                  ROI
                </p>
                <small class="text-muted">Retorno sobre investimento</small>
              </div>
            </b-card>
          </b-col>

          <b-col cols="6">
            <b-card class="stat-card">
              <div class="stat-icon bg-light-info">
                <feather-icon
                  icon="UserPlusIcon"
                  size="24"
                  class="text-info"
                />
              </div>
              <div class="stat-content">
                <h3 class="stat-value mb-0">
                  {{ formatarMoeda(metricas.cac) }}
                </h3>
                <p class="stat-label text-muted mb-0">
                  CAC
                </p>
                <small class="text-muted">Custo por Aquisição de Cliente</small>
              </div>
            </b-card>
          </b-col>

          <b-col cols="6">
            <b-card class="stat-card">
              <div class="stat-icon bg-light-danger">
                <feather-icon
                  icon="CreditCardIcon"
                  size="24"
                  class="text-danger"
                />
              </div>
              <div class="stat-content">
                <h3 class="stat-value mb-0">
                  {{ formatarMoeda(metricas.custoTotal) }}
                </h3>
                <p class="stat-label text-muted mb-0">
                  Custo Total
                </p>
                <small class="text-muted">{{ formatarMoeda(metricas.custoPorMensagem) }} por msg</small>
              </div>
            </b-card>
          </b-col>

        </b-row>
      </div>

      <!-- Tabela de Campanhas Recentes -->
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            <feather-icon
              icon="ListIcon"
              size="18"
              class="mr-1"
            />
            Campanhas Recentes
          </h5>
          <b-form-input
            v-model="buscaTabela"
            placeholder="Buscar campanha..."
            style="max-width: 300px;"
          />
        </div>

        <b-table
          :items="campanhasFiltradas"
          :fields="camposTabela"
          :per-page="porPagina"
          :current-page="paginaAtual"
          responsive
          striped
          hover
          show-empty
          :busy="carregando"
        >
          <template #table-busy>
            <div class="text-center my-4">
              <b-spinner
                variant="primary"
                class="mr-2"
              />
              <span class="text-muted">Carregando campanhas...</span>
            </div>
          </template>

          <template #empty>
            <div class="text-center py-4">
              <feather-icon
                icon="InboxIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted mb-0">
                Nenhuma campanha encontrada
              </p>
            </div>
          </template>

          <template #cell(nome)="data">
            <div>
              <strong>{{ data.item.nome }}</strong>
              <br>
              <small class="text-muted">{{ data.item.template }}</small>
            </div>
          </template>

          <template #cell(dataEnvio)="data">
            <small>{{ formatarDataHora(data.item.dataEnvio) }}</small>
          </template>

          <template #cell(destinatarios)="data">
            <b-badge variant="light-primary">
              {{ formatarNumero(data.item.destinatarios) }}
            </b-badge>
          </template>

          <template #cell(conversoes)="data">
            <div class="text-center">
              <strong>{{ formatarNumero(data.item.conversoes) }}</strong>
              <br>
              <small class="text-muted">{{ formatarPorcentagem(data.item.taxaConversao) }}</small>
            </div>
          </template>

          <template #cell(custo)="data">
            <span class="text-danger">
              {{ formatarMoeda(data.item.custo) }}
            </span>
          </template>

          <template #cell(receita)="data">
            <strong class="text-success">
              {{ formatarMoeda(data.item.receita) }}
            </strong>
          </template>

          <template #cell(lucro)="data">
            <strong :class="data.item.lucro >= 0 ? 'text-success' : 'text-danger'">
              {{ formatarMoeda(data.item.lucro) }}
            </strong>
          </template>

          <template #cell(roi)="data">
            <b-badge :variant="data.item.roi >= 0 ? 'light-success' : 'light-danger'">
              {{ formatarRoiVezesDePercentual(data.item.roi) }}
            </b-badge>
          </template>
        </b-table>

        <!-- Paginação -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <div class="text-muted">
            Mostrando {{ (paginaAtual - 1) * porPagina + 1 }} a {{ Math.min(paginaAtual * porPagina, campanhasFiltradas.length) }} de {{ campanhasFiltradas.length }} campanhas
          </div>
          <b-pagination
            v-model="paginaAtual"
            :total-rows="campanhasFiltradas.length"
            :per-page="porPagina"
            first-number
            last-number
          />
        </div>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormSelect, BFormInput, BTable, BBadge, BPagination, BProgress, BSpinner, BRow, BCol,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import Chart from 'chart.js'
import { listarCampanhas, buscarMetricasCampanha } from '@/functions/basics/campaign-oficial'
import getStores from '@/functions/basics/stores'

export default {
  name: 'RelatoriosCampanhasSegmentadas',
  components: {
    BCard,
    BButton,
    BFormSelect,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BProgress,
    BSpinner,
    BRow,
    BCol,
    FeatherIcon,
  },
  data() {
    return {
      carregando: false,
      carregandoInicial: true,
      storesList: [],
      storeUuid: null,
      lojaAtual: null,
      buscaTabela: '',
      paginaAtual: 1,
      porPagina: 10,

      filtros: {
        periodo: '30',
        dataInicio: '',
        dataFim: '',
        status: 'todos',
      },

      periodosOptions: [
        { value: 'hoje', text: 'Hoje' },
        { value: '7', text: 'Últimos 7 dias' },
        { value: '30', text: 'Últimos 30 dias' },
        { value: '90', text: 'Últimos 90 dias' },
        { value: 'personalizado', text: 'Período Personalizado' },
      ],

      statusOptions: [
        { value: 'todos', text: 'Todos os Status' },
        { value: 'Entregue', text: 'Entregue' },
        { value: 'Lida', text: 'Lida' },
        { value: 'Erro', text: 'Erro' },
        { value: 'Pendente', text: 'Pendente' },
      ],

      metricas: {
        totalCampanhas: 0,
        mensagensEnviadas: 0,
        mensagensEntregues: 0,
        taxaEntrega: 0,
        mensagensLidas: 0,
        taxaLeitura: 0,
        totalConversoes: 0,
        taxaConversao: 0,
        receitaTotal: 0,
        ticketMedio: 0,
        custoTotal: 0,
        custoPorMensagem: 0,
        lucroTotal: 0,
        roi: 0,
        cac: 0,
      },

      campanhas: [],

      camposTabela: [
        { key: 'nome', label: 'Campanha', sortable: true },
        { key: 'dataEnvio', label: 'Data', sortable: true },
        {
          key: 'destinatarios', label: 'Enviadas', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
        {
          key: 'conversoes', label: 'Conversões', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
        {
          key: 'custo', label: 'Custo', sortable: true, thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'receita', label: 'Receita', sortable: true, thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'lucro', label: 'Lucro', sortable: true, thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'roi', label: 'ROI', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
      ],

      chartLinhaInstance: null,
      chartPizzaInstance: null,
      chartBarrasInstance: null,
    }
  },
  computed: {
    campanhasFiltradas() {
      let resultado = [...this.campanhas]

      if (this.buscaTabela) {
        const termo = this.buscaTabela.toLowerCase()
        resultado = resultado.filter(c => c.nome.toLowerCase().includes(termo)
          || c.template.toLowerCase().includes(termo))
      }

      if (this.filtros.status !== 'todos') {
        resultado = resultado.filter(c => c.status === this.filtros.status)
      }

      return resultado
    },
  },
  async mounted() {
    await this.carregarLojas()
  },
  beforeDestroy() {
    if (this.chartLinhaInstance) this.chartLinhaInstance.destroy()
    if (this.chartPizzaInstance) this.chartPizzaInstance.destroy()
    if (this.chartBarrasInstance) this.chartBarrasInstance.destroy()
  },
  methods: {
    async carregarLojas() {
      try {
        this.carregandoInicial = true

        console.log('📊 Carregando lojas...')
        const stores = await getStores()
        console.log('📊 Lojas recebidas:', stores)
        this.storesList = stores || []

        // Se tiver apenas uma loja, selecionar automaticamente
        if (this.storesList.length === 1) {
          console.log('📊 Selecionando única loja automaticamente:', this.storesList[0])
          this.carregandoInicial = false
          await this.selecionarLoja(this.storesList[0])
        } else if (this.storesList.length === 0) {
          this.$bvToast.toast('Nenhuma loja encontrada', {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
          })
          this.carregandoInicial = false
        } else {
          console.log('📊 Múltiplas lojas encontradas, aguardando seleção')
          this.carregandoInicial = false
        }
      } catch (error) {
        console.error('❌ Erro ao carregar lojas:', error)
        this.$bvToast.toast('Erro ao carregar lojas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.carregandoInicial = false
      }
    },

    async selecionarLoja(store) {
      this.storeUuid = store.uuid
      this.lojaAtual = store
      await this.carregarDados()
    },

    trocarLoja() {
      this.storeUuid = null
      this.lojaAtual = null
      this.campanhas = []
      this.resetarMetricas()
    },

    async carregarDados() {
      if (!this.storeUuid) return

      try {
        this.carregando = true

        // Buscar campanhas da loja
        console.log('📊 Buscando campanhas para loja:', this.storeUuid)
        const campanhasApi = await listarCampanhas(this.storeUuid)
        console.log('📊 Campanhas recebidas:', campanhasApi)

        if (!campanhasApi || campanhasApi.length === 0) {
          console.log('📊 Nenhuma campanha encontrada')
          this.campanhas = []
          this.resetarMetricas()
          this.$nextTick(() => {
            this.criarGraficos()
          })
          return
        }

        // Buscar métricas de cada campanha em paralelo
        console.log('📊 Buscando métricas de cada campanha...')
        const campanhasComMetricas = await Promise.all(
          campanhasApi.map(async campanha => {
            try {
              const stats = await buscarMetricasCampanha(this.storeUuid, campanha.id)
              console.log(`📊 Métricas da campanha ${campanha.id}:`, stats)
              return { ...campanha, stats }
            } catch (error) {
              console.warn(`⚠️ Erro ao buscar métricas da campanha ${campanha.id}:`, error)
              return { ...campanha, stats: null }
            }
          }),
        )

        // Transformar dados da API para o formato do componente
        // Estrutura da API: { success, data: { campanha, execucoes, audiencia, mensagens, conversoes, roi } }
        this.campanhas = campanhasComMetricas.map(c => {
          const statsData = c.stats?.data || c.stats || {}
          const mensagens = statsData.mensagens || {}
          const conversoes = statsData.conversoes || {}
          const roi = statsData.roi || {}
          const audiencia = statsData.audiencia || {}

          // Extrair métricas
          const destinatarios = parseInt(mensagens.enviadas) || parseInt(audiencia.total_leads) || 0
          const entregues = parseInt(mensagens.entregues) || 0
          const lidas = parseInt(mensagens.lidas) || 0
          const taxaEntrega = parseFloat(mensagens.taxa_entrega) || 0
          const taxaLeitura = parseFloat(mensagens.taxa_leitura) || 0

          // Financeiro
          const receita = parseFloat(conversoes.valor_total) || parseFloat(roi.receita_total) || 0
          const custo = parseFloat(roi.custo_estimado) || 0
          const lucro = parseFloat(roi.lucro) || 0
          const roiPercent = parseFloat(roi.roi_percentual) || 0
          const totalConversoes = parseInt(conversoes.total) || 0
          const ticketMedio = parseFloat(conversoes.ticket_medio) || 0
          const taxaConversao = parseFloat(conversoes.taxa_conversao) || 0

          return {
            id: c.id,
            nome: c.name || 'Sem nome',
            template: c.template_name || 'Template',
            dataEnvio: c.send_date || c.created_at,
            destinatarios,
            entregues,
            lidas,
            taxaEntrega,
            taxaLeitura,
            receita,
            custo,
            lucro,
            roi: roiPercent,
            conversoes: totalConversoes,
            ticketMedio,
            taxaConversao,
            status: this.mapearStatus(c.status),
          }
        })

        console.log('📊 Campanhas processadas com métricas:', this.campanhas)

        // Calcular métricas agregadas
        this.calcularMetricas()

        // Criar gráficos
        this.$nextTick(() => {
          this.criarGraficos()
        })
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error)
        this.$bvToast.toast('Erro ao carregar campanhas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },

    mapearStatus(status) {
      const statusMap = {
        completed: 'Entregue',
        sent: 'Entregue',
        delivered: 'Entregue',
        read: 'Lida',
        failed: 'Erro',
        error: 'Erro',
        pending: 'Pendente',
        scheduled: 'Pendente',
        running: 'Pendente',
      }
      return statusMap[status?.toLowerCase()] || status || 'Pendente'
    },

    calcularMetricas() {
      // Totais
      this.metricas.totalCampanhas = this.campanhas.length
      this.metricas.mensagensEnviadas = this.campanhas.reduce((sum, c) => sum + (c.destinatarios || 0), 0)
      this.metricas.mensagensEntregues = this.campanhas.reduce((sum, c) => sum + (c.entregues || 0), 0)
      this.metricas.mensagensLidas = this.campanhas.reduce((sum, c) => sum + (c.lidas || 0), 0)

      // Taxas médias ponderadas
      if (this.metricas.mensagensEnviadas > 0) {
        this.metricas.taxaEntrega = (this.metricas.mensagensEntregues / this.metricas.mensagensEnviadas) * 100
      }
      if (this.metricas.mensagensEntregues > 0) {
        this.metricas.taxaLeitura = (this.metricas.mensagensLidas / this.metricas.mensagensEntregues) * 100
      }

      // Financeiro - somar de todas as campanhas
      this.metricas.receitaTotal = this.campanhas.reduce((sum, c) => sum + (c.receita || 0), 0)
      this.metricas.custoTotal = this.campanhas.reduce((sum, c) => sum + (c.custo || 0), 0)
      this.metricas.totalConversoes = this.campanhas.reduce((sum, c) => sum + (c.conversoes || 0), 0)

      // Lucro total
      this.metricas.lucroTotal = this.metricas.receitaTotal - this.metricas.custoTotal

      // Custo por mensagem
      if (this.metricas.mensagensEnviadas > 0) {
        this.metricas.custoPorMensagem = this.metricas.custoTotal / this.metricas.mensagensEnviadas
      }

      // Taxa de conversão
      if (this.metricas.mensagensEnviadas > 0) {
        this.metricas.taxaConversao = (this.metricas.totalConversoes / this.metricas.mensagensEnviadas) * 100
      }

      // Ticket médio
      if (this.metricas.totalConversoes > 0) {
        this.metricas.ticketMedio = this.metricas.receitaTotal / this.metricas.totalConversoes
      }

      // ROI = (Lucro / Custo) * 100
      if (this.metricas.custoTotal > 0) {
        this.metricas.roi = (this.metricas.lucroTotal / this.metricas.custoTotal) * 100
      } else if (this.metricas.receitaTotal > 0) {
        // Se não teve custo mas teve receita, ROI é infinito (mostramos como 100%)
        this.metricas.roi = 100
      }

      // CAC = Custo Total / Quantidade de Compradores (conversões)
      if (this.metricas.totalConversoes > 0) {
        this.metricas.cac = this.metricas.custoTotal / this.metricas.totalConversoes
      } else {
        this.metricas.cac = 0
      }

      console.log('📊 Métricas calculadas:', this.metricas)
    },

    resetarMetricas() {
      this.metricas = {
        totalCampanhas: 0,
        mensagensEnviadas: 0,
        mensagensEntregues: 0,
        taxaEntrega: 0,
        mensagensLidas: 0,
        taxaLeitura: 0,
        totalConversoes: 0,
        taxaConversao: 0,
        receitaTotal: 0,
        ticketMedio: 0,
        custoTotal: 0,
        custoPorMensagem: 0,
        lucroTotal: 0,
        roi: 0,
        cac: 0,
      }
    },

    getAvatarStyle(store) {
      const colors = ['#7367F0', '#28C76F', '#FF9F43', '#EA5455', '#00CFE8']
      const index = this.storesList.indexOf(store) % colors.length
      return {
        backgroundColor: store.logo ? 'transparent' : colors[index],
      }
    },

    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').substring(0, 2)
        .toUpperCase()
    },

    gerarDadosMockados() {
      const nomes = [
        'Promoção Black Friday',
        'Lançamento Novo Produto',
        'Desconto Progressivo',
        'Campanha de Aniversário',
        'Última Chance - 50% OFF',
        'Frete Grátis Hoje',
        'Combo Especial',
        'Reativação de Clientes',
        'Carrinho Abandonado',
        'Produto de Volta ao Estoque',
        'Oferta Relâmpago',
        'Programa de Indicação',
        'Pesquisa de Satisfação',
        'Novidades da Semana',
        'Produtos Mais Vendidos',
      ]

      const templates = [
        'Template Promocional',
        'Template Informativo',
        'Template Urgência',
        'Template Personalizado',
        'Template VIP',
      ]

      const status = ['Entregue', 'Lida', 'Erro', 'Pendente']

      this.campanhas = Array.from({ length: 50 }, (_, i) => {
        const destinatarios = Math.floor(Math.random() * 1000) + 100
        const taxaEntrega = Math.random() * 20 + 80
        const taxaLeitura = Math.random() * 40 + 40
        const receita = Math.random() * 5000 + 500

        return {
          id: i + 1,
          nome: nomes[Math.floor(Math.random() * nomes.length)],
          template: templates[Math.floor(Math.random() * templates.length)],
          dataEnvio: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
          destinatarios,
          taxaEntrega,
          taxaLeitura,
          receita,
          status: status[Math.floor(Math.random() * status.length)],
        }
      })

      this.campanhas.sort((a, b) => new Date(b.dataEnvio) - new Date(a.dataEnvio))
    },

    criarGraficos() {
      this.criarGraficoLinha()
      this.criarGraficoPizza()
      this.criarGraficoBarras()
    },

    criarGraficoLinha() {
      const ctx = this.$refs.chartLinha
      if (!ctx) return

      // Criar array com os últimos 30 dias
      const labels = []
      const campanhasPorDia = {}

      for (let i = 29; i >= 0; i--) {
        const dataObj = new Date()
        dataObj.setDate(dataObj.getDate() - i)
        const dataFormatada = dataObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
        labels.push(dataFormatada)
        campanhasPorDia[dataFormatada] = 0
      }

      // Agrupar campanhas por data de envio
      this.campanhas.forEach(c => {
        if (c.dataEnvio) {
          const dataEnvio = new Date(c.dataEnvio)
          const dataFormatada = dataEnvio.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
          if (campanhasPorDia.hasOwnProperty(dataFormatada)) {
            campanhasPorDia[dataFormatada]++
          }
        }
      })

      // Converter para array de dados na ordem dos labels
      const data = labels.map(label => campanhasPorDia[label] || 0)

      if (this.chartLinhaInstance) {
        this.chartLinhaInstance.destroy()
      }

      this.chartLinhaInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Campanhas Enviadas',
            data,
            borderColor: '#7367F0',
            backgroundColor: 'rgba(115, 103, 240, 0.1)',
            tension: 0.4,
            fill: true,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
              },
            },
          },
        },
      })
    },

    criarGraficoPizza() {
      const ctx = this.$refs.chartPizza
      if (!ctx) return

      if (this.chartPizzaInstance) {
        this.chartPizzaInstance.destroy()
      }

      // Usar dados reais das métricas
      const enviadas = this.metricas.mensagensEnviadas || 0
      const entregues = this.metricas.mensagensEntregues || 0
      const lidas = this.metricas.mensagensLidas || 0
      const naoEntregues = enviadas - entregues

      // Evitar gráfico vazio
      const temDados = enviadas > 0 || entregues > 0 || lidas > 0

      this.chartPizzaInstance = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Entregues', 'Lidas', 'Não Entregues'],
          datasets: [{
            data: temDados ? [entregues, lidas, naoEntregues] : [1, 0, 0],
            backgroundColor: [
              '#28C76F',
              '#00CFE8',
              '#EA5455',
            ],
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
            },
            tooltip: {
              callbacks: {
                label: context => {
                  const value = context.raw || 0
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                  return `${context.label}: ${this.formatarNumero(value)} (${percent}%)`
                },
              },
            },
          },
        },
      })
    },

    criarGraficoBarras() {
      const ctx = this.$refs.chartBarras
      if (!ctx) return

      // Ordenar por conversões (não por receita)
      const campanhasTop = [...this.campanhas]
        .sort((a, b) => (b.conversoes || 0) - (a.conversoes || 0))
        .slice(0, 5)

      if (this.chartBarrasInstance) {
        this.chartBarrasInstance.destroy()
      }

      this.chartBarrasInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: campanhasTop.map(c => c.nome.substring(0, 20) + (c.nome.length > 20 ? '...' : '')),
          datasets: [{
            label: 'Conversões',
            data: campanhasTop.map(c => c.conversoes || 0),
            backgroundColor: '#28C76F',
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0,
              },
            },
          },
        },
      })
    },

    aplicarFiltros() {
      this.paginaAtual = 1
    },

    async atualizarDados() {
      await this.carregarDados()
      this.$bvToast.toast('Dados atualizados com sucesso!', {
        title: 'Sucesso',
        variant: 'success',
        solid: true,
        autoHideDelay: 2000,
      })
    },

    exportarRelatorio() {
      const linhas = []
      const separador = ';'

      // Cabeçalho do relatório
      linhas.push(['RELATÓRIO DE CAMPANHAS SEGMENTADAS'])
      linhas.push([`Loja: ${this.lojaAtual?.name || 'N/A'}`])
      linhas.push([`Período: ${this.filtros.periodo === 'personalizado' ? `${this.filtros.dataInicio} a ${this.filtros.dataFim}` : this.periodosOptions.find(p => p.value === this.filtros.periodo)?.text || this.filtros.periodo}`])
      linhas.push([`Gerado em: ${new Date().toLocaleString('pt-BR')}`])
      linhas.push([])

      // Seção de Métricas Consolidadas
      linhas.push(['MÉTRICAS CONSOLIDADAS'])
      linhas.push(['Métrica', 'Valor'])
      linhas.push(['Total de Campanhas', this.metricas.totalCampanhas])
      linhas.push(['Mensagens Enviadas', this.metricas.mensagensEnviadas])
      linhas.push(['Mensagens Entregues', this.metricas.mensagensEntregues])
      linhas.push(['Taxa de Entrega (%)', (this.metricas.taxaEntrega || 0).toFixed(2)])
      linhas.push(['Mensagens Lidas', this.metricas.mensagensLidas])
      linhas.push(['Taxa de Leitura (%)', (this.metricas.taxaLeitura || 0).toFixed(2)])
      linhas.push(['Total de Conversões', this.metricas.totalConversoes])
      linhas.push(['Taxa de Conversão (%)', (this.metricas.taxaConversao || 0).toFixed(2)])
      linhas.push(['Custo Total (R$)', (this.metricas.custoTotal || 0).toFixed(2)])
      linhas.push(['Custo por Mensagem (R$)', (this.metricas.custoPorMensagem || 0).toFixed(4)])
      linhas.push(['Receita Total (R$)', (this.metricas.receitaTotal || 0).toFixed(2)])
      linhas.push(['Ticket Médio (R$)', (this.metricas.ticketMedio || 0).toFixed(2)])
      linhas.push(['Lucro Total (R$)', (this.metricas.lucroTotal || 0).toFixed(2)])
      linhas.push(['ROI (x)', this.exportarRoiVezesConsolidado()])
      linhas.push(['CAC - Custo por Aquisição (R$)', (this.metricas.cac || 0).toFixed(2)])
      linhas.push([])

      // Seção de Detalhamento por Campanha
      linhas.push(['DETALHAMENTO POR CAMPANHA'])
      linhas.push([
        'Nome da Campanha',
        'Template',
        'Data de Envio',
        'Status',
        'Destinatários',
        'Entregues',
        'Lidas',
        'Taxa Entrega (%)',
        'Taxa Leitura (%)',
        'Conversões',
        'Taxa Conversão (%)',
        'Custo (R$)',
        'Receita (R$)',
        'Lucro (R$)',
        'ROI (x)',
        'Ticket Médio (R$)',
      ])

      this.campanhasFiltradas.forEach(campanha => {
        linhas.push([
          campanha.nome || '',
          campanha.template || '',
          this.formatarDataHora(campanha.dataEnvio) || '',
          campanha.status || '',
          campanha.destinatarios || 0,
          campanha.entregues || 0,
          campanha.lidas || 0,
          (campanha.taxaEntrega || 0).toFixed(2),
          (campanha.taxaLeitura || 0).toFixed(2),
          campanha.conversoes || 0,
          (campanha.taxaConversao || 0).toFixed(2),
          (campanha.custo || 0).toFixed(2),
          (campanha.receita || 0).toFixed(2),
          (campanha.lucro || 0).toFixed(2),
          this.exportarRoiVezesCampanha(campanha),
          (campanha.ticketMedio || 0).toFixed(2),
        ])
      })

      // Converter para CSV com separador ;
      const csv = `\uFEFF${linhas.map(linha => linha.map(celula => {
        const valor = String(celula)
        if (valor.includes(separador) || valor.includes('"') || valor.includes('\n')) {
          return `"${valor.replace(/"/g, '""')}"`
        }
        return valor
      }).join(separador)).join('\n')}`

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      const dataAtual = new Date().toISOString().split('T')[0]
      link.setAttribute('href', url)
      link.setAttribute('download', `relatorio-campanhas-${this.lojaAtual?.name || 'loja'}-${dataAtual}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$bvToast.toast('Relatório exportado com sucesso!', {
        title: 'Sucesso',
        variant: 'success',
        solid: true,
        autoHideDelay: 2000,
      })
    },

    getProgressVariant(valor) {
      if (valor >= 80) return 'success'
      if (valor >= 60) return 'info'
      if (valor >= 40) return 'warning'
      return 'danger'
    },

    getStatusVariant(status) {
      const variants = {
        Entregue: 'light-success',
        Lida: 'light-info',
        Erro: 'light-danger',
        Pendente: 'light-warning',
      }
      return variants[status] || 'light-secondary'
    },

    formatarNumero(valor) {
      if (valor === null || valor === undefined) return '0'
      return new Intl.NumberFormat('pt-BR').format(valor)
    },

    formatarPorcentagem(valor) {
      if (valor === null || valor === undefined) return '0%'
      return `${parseFloat(valor).toFixed(1)}%`
    },

    /** roi_percentual = (lucro/custo)×100 → ROI(x) = receita/custo = (lucro/custo) + 1 = p/100 + 1. */
    formatarRoiVezesDePercentual(roiPercent) {
      if (roiPercent === null || roiPercent === undefined || roiPercent === '') return '—'
      const p = typeof roiPercent === 'string' ? parseFloat(roiPercent) : roiPercent
      if (!Number.isFinite(p)) return '—'
      const mult = p / 100 + 1
      return `${mult.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}x`
    },

    formatarRoiVezesMetricasConsolidadas() {
      const {
        lucroTotal, custoTotal, receitaTotal, roi,
      } = this.metricas
      if (custoTotal > 0 && Number.isFinite(receitaTotal)) {
        const mult = receitaTotal / custoTotal
        return `${mult.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}x`
      }
      if (custoTotal <= 0 && receitaTotal > 0) return '—'
      return this.formatarRoiVezesDePercentual(roi)
    },

    exportarRoiVezesConsolidado() {
      const {
        lucroTotal, custoTotal, receitaTotal, roi,
      } = this.metricas
      if (custoTotal > 0 && Number.isFinite(receitaTotal)) {
        return (receitaTotal / custoTotal).toFixed(2)
      }
      if (custoTotal <= 0 && receitaTotal > 0) return ''
      const p = roi
      if (p === null || p === undefined || !Number.isFinite(Number(p))) return ''
      return (Number(p) / 100 + 1).toFixed(2)
    },

    exportarRoiVezesCampanha(campanha) {
      const p = campanha && campanha.roi
      if (p === null || p === undefined || p === '' || !Number.isFinite(Number(p))) return ''
      return (Number(p) / 100 + 1).toFixed(2)
    },

    formatarMoeda(valor) {
      if (valor === null || valor === undefined) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(valor)
    },

    formatarDataHora(dataISO) {
      if (!dataISO) return ''
      const data = new Date(dataISO)
      return data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.relatorios-campanhas {
  padding: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

/* Containers dos gráficos */
.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}

.chart-container-pizza {
  position: relative;
  height: 280px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-container-pizza canvas {
  max-width: 280px !important;
  max-height: 280px !important;
}

@media (max-width: 991px) {
  .chart-container {
    height: 250px;
  }

  .chart-container-pizza {
    height: 250px;
  }

  .chart-container-pizza canvas {
    max-width: 220px !important;
    max-height: 220px !important;
  }
}

.stores-grid-select {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 400px;
  margin: 0 auto;
}

.store-select-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #fff;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.store-select-card:hover {
  border-color: #7367f0;
  background: #f8f7ff;
  transform: translateX(4px);
}

.store-avatar-small {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.store-avatar-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.store-name-small {
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
}

.gap-2 {
  gap: 0.5rem;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.stat-card {
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-card .card-body {
  display: flex;
  align-items: center;
  padding: 1.25rem;
}

.stat-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  margin-right: 1rem;
  flex-shrink: 0;
}

.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12);
}

.bg-light-success {
  background-color: rgba(40, 199, 111, 0.12);
}

.bg-light-info {
  background-color: rgba(0, 207, 232, 0.12);
}

.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12);
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .flex-wrap {
    flex-direction: column;
    align-items: stretch !important;
  }

  .flex-wrap > div {
    width: 100%;
  }
}
</style>
